import { Loader2, RotateCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { adminClient, type AdminFabricPlacementState, type AdminOrgFabric } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminMetaItem } from '@/components/shell/AdminMetaItem';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminSection } from '@/components/shell/AdminSection';
import { Button } from '@/components/ui/button';
import { LoadError } from '@/components/ui/load-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { StatusTone } from '@/lib/runTelemetry';
import { useOrgScope } from '@/pages/org/OrgLayout';

const POLL_MS = 3000;

export const FABRIC_STATE_LABELS: Record<AdminFabricPlacementState, string> = {
  pending: 'Waiting to start',
  provisioning: 'Creating namespace',
  ready: 'Ready',
  failed: 'Failed',
};

export const FABRIC_STATE_TONES: Record<AdminFabricPlacementState, StatusTone> = {
  pending: 'active',
  provisioning: 'active',
  ready: 'success',
  failed: 'danger',
};

function isSettling(view: AdminOrgFabric | undefined): boolean {
  const state = view?.placement?.state;
  return state === 'pending' || state === 'provisioning';
}

export function OrgFabricPage() {
  const { orgId } = useOrgScope();
  const [view, setView] = useState<AdminOrgFabric | undefined>();
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    const next = await adminClient.getOrgFabric(orgId);
    setView(next);
    setSelectedId((current) => current ?? next.placement?.fabricId ?? next.defaultFabricId ?? undefined);
    return next;
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    load()
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load the Fabric placement');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load, reloadKey]);

  useEffect(() => {
    if (!isSettling(view)) return undefined;
    const timer = window.setTimeout(() => {
      void load().catch(() => undefined);
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [view, load]);

  const place = async (fabricId: string | undefined) => {
    setSaving(true);
    try {
      const next = await adminClient.putOrgFabric(orgId, { fabricId: fabricId ?? null });
      setView(next);
      if (next.provisioningStarted === false) {
        toast.error('Placed, but the namespace could not be started. Try again in a moment.');
      } else {
        toast.success('Creating the namespace');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to place the organization');
    } finally {
      setSaving(false);
    }
  };

  const placement = view?.placement ?? null;
  const fabrics = view?.fabrics ?? [];
  const current = placement ? fabrics.find((fabric) => fabric.id === placement.fabricId) : undefined;
  const choosable = fabrics.filter((fabric) => fabric.acceptingOrgs || fabric.id === placement?.fabricId);
  const canChoose = !placement || placement.state !== 'ready';
  const canRetry = placement && (placement.state === 'failed' || placement.state === 'pending');

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Fabric"
        description="The Fabric cluster this organization runs on, and the namespace it has there. Its published app backends deploy into that namespace."
      />

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading the Fabric placement
        </div>
      ) : error ? (
        <LoadError message={error} onRetry={() => setReloadKey((key) => key + 1)} />
      ) : fabrics.length === 0 ? (
        <AdminSection title="No Fabric clusters">
          <p className="text-sm text-muted-foreground">
            This Studio environment has no Fabric clusters in its registry, so organizations are not placed on one.
          </p>
        </AdminSection>
      ) : (
        <div className="space-y-6">
          <AdminSection
            title="Placement"
            description={
              placement
                ? 'Where this organization lives. Moving to another cluster is possible until its namespace exists.'
                : 'Not placed yet. Organizations are placed on the default cluster when they get their first Studio seat, or here.'
            }
            actions={
              canRetry ? (
                <Button type="button" variant="outline" size="sm" onClick={() => void place(placement.fabricId)} disabled={saving}>
                  <RotateCw className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                  Retry
                </Button>
              ) : null
            }
          >
            {placement ? (
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-4">
                <AdminMetaItem label="Cluster">{current?.displayName ?? placement.fabricId}</AdminMetaItem>
                <AdminMetaItem label="Cluster id">
                  <span className="font-mono text-xs">{placement.fabricId}</span>
                </AdminMetaItem>
                <AdminMetaItem label="Namespace">
                  <span className="font-mono text-xs">{placement.namespace}</span>
                </AdminMetaItem>
                <AdminMetaItem label="State">
                  <StatusPill tone={FABRIC_STATE_TONES[placement.state]}>{FABRIC_STATE_LABELS[placement.state]}</StatusPill>
                </AdminMetaItem>
                <AdminMetaItem label="Region">{current ? `${current.region} · ${current.account}` : '—'}</AdminMetaItem>
                <AdminMetaItem label="Apps domain">{current?.appsDomain ?? '—'}</AdminMetaItem>
                <AdminMetaItem label="Attempts">{placement.attempts}</AdminMetaItem>
                <AdminMetaItem label="Ready since">
                  {placement.readyAt ? new Date(placement.readyAt).toLocaleString() : '—'}
                </AdminMetaItem>
              </dl>
            ) : null}
            {placement?.state === 'failed' && placement.error ? (
              <p className="mt-4 rounded-lg border border-destructive-border bg-destructive-bg px-3 py-2 text-xs text-destructive-foreground">
                {placement.error}
              </p>
            ) : null}
            {canChoose ? (
              <div className={placement ? 'mt-5 flex flex-wrap items-end gap-3' : 'flex flex-wrap items-end gap-3'}>
                <div className="w-full max-w-sm">
                  <Select value={selectedId} onValueChange={setSelectedId} disabled={saving}>
                    <SelectTrigger aria-label="Fabric cluster">
                      <SelectValue placeholder="Choose a cluster" />
                    </SelectTrigger>
                    <SelectContent>
                      {choosable.map((fabric) => (
                        <SelectItem key={fabric.id} value={fabric.id}>
                          {fabric.displayName} · {fabric.id}
                          {fabric.default ? ' (default)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  onClick={() => void place(selectedId)}
                  disabled={saving || !selectedId || (placement?.fabricId === selectedId && isSettling(view))}
                >
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                  {placement ? 'Place here' : 'Place and create namespace'}
                </Button>
              </div>
            ) : null}
          </AdminSection>
        </div>
      )}
    </div>
  );
}
